import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Inventory as InventoryIcon,
  Description as DatasheetIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CopiedIcon,
} from '@mui/icons-material';
import { partsService } from '../services/partsService';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import marketplaceService from '../services/marketplaceService';

const PartDetailPage = () => {
  const { id, partNumber } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useAuth();
  const { addToCart } = useCart();

  // State from navigation or default
  const initialPart = location.state?.part;

  // Local state — initialised after part loads
  const [quantity, setQuantity] = useState(1);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  // Query for part details
  const {
    data: partVariants,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['partDetails', id, partNumber],
    queryFn: async () => {
      if (id) {
        // Fetch the exact listing by component ID — handles multiple sellers with same MPN
        const part = await partsService.getPartById(id);
        return part ? [part] : [];
      }
      // Fallback: search by MPN (old /parts/:partNumber URL)
      return partsService.getPartDetails(decodeURIComponent(partNumber));
    },
    initialData: initialPart ? [initialPart] : undefined,
    staleTime: 5 * 60 * 1000,
  });

  // Get the primary part (best available option)
  const primaryPart = partVariants?.[0];
  const minQty = primaryPart?.minimumOrderQuantity || 1;
  const isOwnListing = user && primaryPart?.sellerId && String(user.id) === String(primaryPart.sellerId);

  // Keep quantity >= minQty when part loads
  React.useEffect(() => {
    if (minQty > 1) setQuantity(minQty);
  }, [minQty]);

  // Handlers
  const handleQuantityChange = (event) => {
    const value = Math.max(minQty, parseInt(event.target.value) || minQty);
    setQuantity(value);
  };

  const handleAddToCart = () => {
    if (primaryPart && !isOwnListing) {
      addToCart(primaryPart, quantity);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user || !primaryPart?.componentId || favoriteLoading) return;
    setFavoriteLoading(true);
    try {
      if (isFavorited) {
        await marketplaceService.removeSaved(primaryPart.componentId);
        setIsFavorited(false);
      } else {
        await marketplaceService.saveComponent(primaryPart.componentId);
        setIsFavorited(true);
      }
    } catch {
      // silently ignore — favorite state stays unchanged
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleShare = () => {
    setShowShareDialog(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };



  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (isError || !partVariants || partVariants.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error?.message || 'Part not found or no longer available.'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/')}
              sx={{ textDecoration: 'none' }}
            >
              Home
            </Link>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/parts')}
              sx={{ textDecoration: 'none' }}
            >
              Parts
            </Link>
            <Typography color="text.primary" variant="body2">
              {primaryPart?.partNumber}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              variant="outlined"
            >
              Back
            </Button>

            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              {user && (
                <Tooltip title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}>
                  <IconButton onClick={handleToggleFavorite} disabled={favoriteLoading}>
                    {isFavorited ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Share">
                <IconButton onClick={handleShare}>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Main Content Row */}
          <Grid container spacing={2}>
            {/* Left Column - Product Image */}
            <Grid item xs={12} md={3} lg={2.5}>
              <Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 280,
                    bgcolor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    mb: 1.5
                  }}
                >
                  {primaryPart?.thumbnailUrl ? (
                    <img
                      src={primaryPart.thumbnailUrl}
                      alt={primaryPart.displayName}
                      style={{
                        maxWidth: '85%',
                        maxHeight: '85%',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <InventoryIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                  )}
                </Box>

                {/* Thumbnail Navigation */}
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-start', mb: 1 }}>
                  <Box
                    sx={{
                      width: 45,
                      height: 45,
                      bgcolor: '#f0f0f0',
                      border: '2px solid #1976d2',
                      borderRadius: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    {primaryPart?.thumbnailUrl ? (
                      <img
                        src={primaryPart.thumbnailUrl}
                        alt="thumbnail"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <InventoryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    )}
                  </Box>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.3 }}>
                  Image shown is a representation only. Exact specifications should be obtained from the product data sheet.
                </Typography>
              </Box>
            </Grid>

            {/* Center Column - Product Details */}
            <Grid item xs={12} md={6} lg={6.5}>
              <Paper>
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="h4" component="h1" sx={{ mb: 0, fontWeight: 'bold', fontSize: '1.75rem' }}>
                    {primaryPart?.partNumber}
                  </Typography>
                </Box>

                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                        <TableCell sx={{ fontWeight: 'bold', width: '30%', py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          PartzFlow Part Number
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          {primaryPart?.partNumber || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                        <TableCell sx={{ fontWeight: 'bold', py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          Manufacturer
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          {primaryPart?.manufacturer || '—'}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                        <TableCell sx={{ fontWeight: 'bold', py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          Manufacturer Product Number
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          {primaryPart?.partNumber}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                        <TableCell sx={{ fontWeight: 'bold', py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          Description
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          {primaryPart?.description}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                        <TableCell sx={{ fontWeight: 'bold', py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          Manufacturer Standard Lead Time
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          {primaryPart?.leadTimeWeeks != null ? `${primaryPart.leadTimeWeeks} week(s)` : 'Contact seller'}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                        <TableCell sx={{ fontWeight: 'bold', py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          Customer Reference
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          <TextField
                            size="small"
                            placeholder="Enter reference"
                            sx={{ width: '180px', '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                        </TableCell>
                      </TableRow>
                      {primaryPart?.detailedDescription && (
                        <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                          <TableCell sx={{ fontWeight: 'bold', py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                            Detailed Description
                          </TableCell>
                          <TableCell sx={{ py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                            {primaryPart.detailedDescription}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                        <TableCell sx={{ fontWeight: 'bold', py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          Datasheet
                        </TableCell>
                        <TableCell sx={{ py: 1, borderBottom: '1px solid #e0e0e0', fontSize: '0.875rem' }}>
                          {primaryPart?.datasheet ? (
                            <Link
                              href={primaryPart.datasheet}
                              target="_blank"
                              color="primary"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', fontSize: '0.875rem' }}
                            >
                              <DatasheetIcon sx={{ fontSize: 14 }} />
                              Datasheet
                            </Link>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Right Column - Purchase Panel */}
            <Grid item xs={12} md={3} lg={3}>
              <Box>
                {/* Stock Status Card */}
                <Card sx={{ mb: 1.5 }}>
                  <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{
                      mb: 0.5, fontWeight: 'bold', fontSize: '1.1rem',
                      color: (primaryPart?.availability || 0) > 0 ? 'success.main' : 'error.main',
                    }}>
                      {(primaryPart?.availability || 0) > 0
                        ? `In Stock: ${primaryPart.availability.toLocaleString()}`
                        : 'Out of Stock'}
                    </Typography>
                    {primaryPart?.backOrderAllowed && (
                      <Typography variant="caption" color="text.secondary">Back orders accepted</Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Quantity and Actions */}
                <Card sx={{ mb: 1.5 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.85rem' }}>
                      QUANTITY
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={quantity}
                      onChange={handleQuantityChange}
                      inputProps={{ min: minQty, type: 'number' }}
                      size="small"
                      sx={{ mb: 1.5, '& .MuiInputBase-input': { fontSize: '0.9rem', py: 0.75 } }}
                    />

                    {minQty > 1 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.72rem' }}>
                        Min. order quantity: {minQty}
                      </Typography>
                    )}

                    {isOwnListing ? (
                      <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem', py: 0.5 }}>
                        This is your own listing
                      </Alert>
                    ) : (
                      <Button
                        fullWidth variant="contained" color="error" size="small"
                        sx={{ mb: 1.5, textTransform: 'none', fontSize: '0.8rem', py: 0.75 }}
                        onClick={handleAddToCart}
                        disabled={!primaryPart?.inStock}
                      >
                        {primaryPart?.inStock ? 'Add to Cart' : 'Out of Stock'}
                      </Button>
                    )}

                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}>
                      All prices are in USD
                    </Typography>
                  </CardContent>
                </Card>

                {/* Cut Tape Pricing */}
                <Card sx={{ mb: 1.5 }}>
                  <Box sx={{ p: 0.75, bgcolor: 'primary.main' }}>
                    <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '0.85rem' }}>
                      Cut Tape (CT)
                    </Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem', py: 0.5 }}>QUANTITY</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem', py: 0.5 }}>UNIT PRICE</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem', py: 0.5 }}>EXT PRICE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {primaryPart?.priceBreaks?.length > 0
                        ? primaryPart.priceBreaks.slice(0, 5).map((pb, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                              {(pb.quantity ?? pb.break_quantity)?.toLocaleString()}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                              {formatPrice(pb.price ?? pb.unit_price)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                              {formatPrice((pb.price ?? pb.unit_price) * (pb.quantity ?? pb.break_quantity))}
                            </TableCell>
                          </TableRow>
                        ))
                        : (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ fontSize: '0.75rem', py: 1, textAlign: 'center', color: 'text.secondary' }}>
                              Contact seller for pricing
                            </TableCell>
                          </TableRow>
                        )
                      }
                    </TableBody>
                  </Table>
                  <Typography variant="caption" sx={{ p: 1, display: 'block', fontSize: '0.7rem', borderTop: '1px solid #e0e0e0' }}>
                    * Standard packaging options available.
                  </Typography>
                </Card>

                {/* Packaging info */}
                {(primaryPart?.packageType || primaryPart?.standardPackQty) && (
                  <Card>
                    <Box sx={{ p: 1, bgcolor: 'grey.100', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        Packaging
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      {primaryPart.packageType && (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          Type: {primaryPart.packageType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                      )}
                      {primaryPart.standardPackQty && (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          Std Pack: {primaryPart.standardPackQty.toLocaleString()} units
                        </Typography>
                      )}
                    </Box>
                  </Card>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Product Attributes Section - Separate from main grid */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Product Attributes
              </Typography>
            </Box>

            {primaryPart?.specifications && Object.keys(primaryPart.specifications).length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Attribute</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(primaryPart.specifications).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{key}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>{value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No product attributes available</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onClose={() => { setShowShareDialog(false); setCopied(false); }}>
        <DialogTitle>Share Part</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Part URL"
            value={window.location.href}
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                    <IconButton onClick={() => copyToClipboard(window.location.href)} edge="end">
                      {copied ? <CopiedIcon color="success" /> : <CopyIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          {copied && (
            <Typography variant="caption" color="success.main">Link copied to clipboard!</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowShareDialog(false); setCopied(false); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartDetailPage;